/**
 * Cross-client error reporter (Web) — safety net + server sink.
 *
 * Catches window-wide uncaught exceptions, unhandled promise rejections and
 * (wrapped) console.error calls, batches them, and posts to the diagnostics
 * sink (Contract 02 §9: POST /api/v1/client-error). Used by both bundles:
 * the SPA (client="spa") and the standalone player IIFE (client="player").
 *
 * Reliability contract (platform-design hand-off 2026-06-18):
 *  - SILENT transport: raw fetch(keepalive) + navigator.sendBeacon on pagehide.
 *    NEVER core/api.js — its toast + 401-redirect on failure would loop.
 *  - Queue + localStorage mirror: survives reload/crash; secrets redacted
 *    before they are ever stored or sent.
 *  - Client-side dedup/throttle by fingerprint (the server additionally
 *    aggregates by fingerprint + count).
 *  - LOOP-GUARD: failures of the reporter itself are never turned into reports.
 */

// zapd sink (DiagnosticsHandlers) lives under the zapd base path — /api/v1/*
// is the PHP router, which does not know this endpoint.
const ENDPOINT = '/api-zapd/v1/client-error';
const QUEUE_KEY = 'zap_err_queue';
const MAX_QUEUE = 50; // persisted cap; drop oldest beyond this
const MAX_BATCH = 50; // server ClientErrorBatch maxItems
const FLUSH_MS = 5000; // periodic flush cadence
const FLUSH_AT = 10; // flush early once the queue reaches this many
const THROTTLE_MS = 5000; // coalesce an identical fingerprint within this window
const BACKOFF_MS = 30000; // pause network flushes this long after a failure
const MAX_BODY = 4096;
const MAX_STACK = 16384;
const MAX_URL = 1024;

let installed = false;
let client = 'spa';
let notify = null; // optional user-facing notifier (SPA passes toast.error)
let sessionId = '';
let clientVersion = '';
let origConsoleError = null;
let reporting = false; // loop-guard: true while flushing / inside a handler
let queue = [];
let flushTimer = 0;
let backoffUntil = 0;
const lastSeen = new Map(); // fingerprint -> last-enqueued ts (client throttle)

const RE_JWT = /eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g;
const RE_BEARER = /(bearer\s+)[A-Za-z0-9._-]{8,}/gi;
const RE_TOKEN = /((?:access_|refresh_)?token=)[^&#\s"']+/gi;
const RE_PW = /("?password"?\s*[:=]\s*"?)[^",&}\s]+/gi;

function redact(s) {
  if (!s) return '';
  return String(s)
    .replace(RE_JWT, '[REDACTED-JWT]')
    .replace(RE_BEARER, '$1[REDACTED]')
    .replace(RE_TOKEN, '$1[REDACTED]')
    .replace(RE_PW, '$1[REDACTED]');
}

function truncate(s, max) {
  s = String(s);
  return s.length > max ? s.slice(0, max) : s;
}

function firstLine(s) {
  const nl = String(s).indexOf('\n');
  return nl === -1 ? String(s) : String(s).slice(0, nl);
}

// FNV-1a — a fast synchronous hash; good enough for a dedup key.
function hash(s) {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return ('0000000' + h.toString(16)).slice(-8);
}

function genSessionId() {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch {
    /* fall through */
  }
  return 'z-' + hash(String(Date.now()) + ':' + Math.random());
}

function loadQueue() {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (Array.isArray(parsed)) queue = parsed.slice(-MAX_QUEUE);
  } catch {
    /* localStorage unavailable / corrupt — start empty */
  }
}

function saveQueue() {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(queue.slice(-MAX_QUEUE)));
  } catch {
    /* quota / private mode — in-memory queue still works */
  }
}

function fingerprintOf(r) {
  return hash(
    (r.client || '') +
      '' +
      (r.name || '') +
      '' +
      firstLine(r.message || '') +
      '' +
      firstLine(r.stack || ''),
  );
}

function enqueue(report) {
  const t = Date.now();
  const prev = lastSeen.get(report.fingerprint);
  if (prev && t - prev < THROTTLE_MS) return; // storm coalesced
  lastSeen.set(report.fingerprint, t);
  if (lastSeen.size > 500) lastSeen.clear();
  queue.push(report);
  if (queue.length > MAX_QUEUE) queue.splice(0, queue.length - MAX_QUEUE);
  saveQueue();
  armFlushTimer();
  if (queue.length >= FLUSH_AT) flush();
}

// Demand-armed periodic flush: the interval only exists while the queue is
// non-empty (error-free sessions get zero 5s-wakeups) and disarms itself one
// tick after the queue drains.
function armFlushTimer() {
  if (flushTimer) return;
  flushTimer = setInterval(() => {
    void flush();
    if (!queue.length) {
      clearInterval(flushTimer);
      flushTimer = 0;
    }
  }, FLUSH_MS);
}

// Network flush (periodic + threshold). Async fetch; keeps the queue on
// failure and backs off. The `reporting` guard prevents re-entrancy and stops
// any console.error raised on this path from being re-enqueued (loop-guard).
async function flush() {
  if (reporting || !queue.length || Date.now() < backoffUntil) return;
  const batch = queue.slice(0, MAX_BATCH);
  const body = JSON.stringify({ reports: batch });
  reporting = true;
  try {
    let ok = false;
    try {
      const r = await fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
        credentials: 'include',
      });
      ok = !!r && r.ok;
    } catch {
      ok = false;
    }
    if (ok) {
      queue.splice(0, batch.length);
      saveQueue();
      backoffUntil = 0;
    } else {
      backoffUntil = Date.now() + BACKOFF_MS;
    }
  } finally {
    reporting = false;
  }
}

// Last-chance flush on page hide — sendBeacon survives unload where fetch may
// not. Bypasses the `reporting` guard on purpose (this is the final attempt).
function flushBeacon() {
  if (!queue.length) return;
  try {
    if (typeof navigator === 'undefined' || !navigator.sendBeacon) return;
    const batch = queue.slice(0, MAX_BATCH);
    const blob = new Blob([JSON.stringify({ reports: batch })], { type: 'application/json' });
    if (navigator.sendBeacon(ENDPOINT, blob)) {
      queue.splice(0, batch.length);
      saveQueue();
    }
  } catch {
    /* swallow — never report a reporting failure */
  }
}

function buildReport(fields) {
  const r = {
    client,
    severity: fields.severity || 'error',
    name: truncate(fields.name || 'Error', 128),
    message: truncate(redact(fields.message || 'unknown error'), MAX_BODY),
    url: truncate(redact((typeof location !== 'undefined' && location.href) || ''), MAX_URL),
    session_id: sessionId,
    ts_client: Date.now(),
  };
  if (fields.stack) r.stack = truncate(redact(fields.stack), MAX_STACK);
  if (fields.source) r.source = truncate(redact(fields.source), 512);
  if (clientVersion) r.client_version = clientVersion;
  r.fingerprint = fingerprintOf(r);
  return r;
}

function describe(err) {
  if (err == null) return 'unknown error';
  if (typeof err === 'string') return err;
  if (err.message) return String(err.message);
  try {
    return String(err);
  } catch {
    return 'unserializable error';
  }
}

/**
 * Report an error explicitly. Safe to call from anywhere (e.g. the player's
 * worker-error bridge). `err` may be an Error, a string, or anything.
 */
export function reportError(err, meta) {
  if (!installed) return;
  try {
    const m = meta || {};
    const fields = {
      severity: m.severity || 'error',
      name: m.name || (err && err.name) || (typeof err === 'string' ? 'Error' : 'Error'),
      message: describe(err),
      stack: (err && err.stack) || m.stack || '',
      source: m.source || '',
    };
    enqueue(buildReport(fields));
  } catch {
    /* never let reporting throw into the caller */
  }
}

function onWindowError(e) {
  // Resource-load errors (img/script/link) fire 'error' with the failing
  // element as target and no .error — skip those, only real JS exceptions.
  if (e.target && e.target !== window) return;
  const err = e.error || e.message;
  if (origConsoleError) origConsoleError('[uncaught error]', err);
  if (notify) {
    try {
      notify(describe(err));
    } catch {
      /* notifier is best-effort */
    }
  }
  try {
    enqueue(
      buildReport({
        name: (e.error && e.error.name) || 'Error',
        message: describe(err),
        stack: e.error && e.error.stack,
        source: e.filename ? e.filename + ':' + (e.lineno || 0) + ':' + (e.colno || 0) : '',
      }),
    );
  } catch {
    /* swallow */
  }
}

function onRejection(e) {
  const reason = e.reason;
  if (origConsoleError) origConsoleError('[unhandledrejection]', reason);
  if (notify) {
    try {
      notify(describe(reason));
    } catch {
      /* best-effort */
    }
  }
  try {
    enqueue(
      buildReport({
        name: (reason && reason.name) || 'UnhandledRejection',
        message: describe(reason),
        stack: reason && reason.stack,
      }),
    );
  } catch {
    /* swallow */
  }
}

// Wrap console.error so the many handled-but-logged failures (api.js,
// stream.js, the SSE buses) are captured too — without touching each site.
function wrapConsoleError() {
  if (!origConsoleError) origConsoleError = console.error.bind(console);
  console.error = function reporterConsoleError(...args) {
    origConsoleError(...args);
    if (reporting) return; // loop-guard
    try {
      const errArg = args.find((a) => a instanceof Error);
      const msg = errArg ? describe(errArg) : args.map((a) => describe(a)).join(' ');
      enqueue(
        buildReport({
          name: errArg ? errArg.name : 'console.error',
          message: msg,
          stack: errArg && errArg.stack,
        }),
      );
    } catch {
      /* swallow */
    }
  };
}

/**
 * Install the global error reporter. Idempotent.
 * @param {{client?: string, notify?: (msg: string) => void, version?: string}} [opts]
 *   client  — surface tag: "spa" (default) or "player".
 *   notify  — optional user-facing notifier for uncaught errors (SPA: toast).
 *   version — optional build/version string for client_version.
 */
export function installGlobalErrorReporter(opts) {
  if (installed) return;
  installed = true;

  const o = opts || {};
  client = o.client || 'spa';
  notify = typeof o.notify === 'function' ? o.notify : null;
  clientVersion =
    o.version || (typeof globalThis !== 'undefined' && globalThis.__ZAP_BUILD__) || '';
  sessionId = genSessionId();

  loadQueue();
  wrapConsoleError();

  window.addEventListener('error', onWindowError);
  window.addEventListener('unhandledrejection', onRejection);
  window.addEventListener('pagehide', flushBeacon);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushBeacon();
  });

  if (queue.length) {
    armFlushTimer();
    flush(); // drain anything restored from a previous session
  }
}

// Test/teardown hook (used by the E2E harness and idempotency tests).
export function _resetErrorReporterForTest() {
  try {
    window.removeEventListener('error', onWindowError);
    window.removeEventListener('unhandledrejection', onRejection);
    window.removeEventListener('pagehide', flushBeacon);
    if (origConsoleError) console.error = origConsoleError;
    if (flushTimer) { clearInterval(flushTimer); flushTimer = 0; }
  } catch {
    /* noop */
  }
  installed = false;
  origConsoleError = null;
  queue = [];
  lastSeen.clear();
  backoffUntil = 0;
}
